/**
 * Logo Component
 * Brand-aware logo with consistent size and placement
 */

import React from 'react';
import { designTokens } from '../../constants/designTokens';
import { useBrand } from '../../contexts/BrandContext';
import { useLanguage } from '../../i18n';

interface LogoProps {
  /** @deprecated No longer used - brand logo is sourced from brand config */
  variant?: 'combo' | 'icon';
  /** @deprecated No longer used - theme is determined by brand config */
  theme?: 'dark' | 'light';
  desktopEnhanced?: boolean;
  className?: string;
}

export const Logo = React.memo(function Logo({
  desktopEnhanced = true,
  className = '',
}: LogoProps) {
  const brand = useBrand();
  const { t } = useLanguage();

  // Brand isolation: No cross-brand fallbacks allowed
  // If brand is not available, we cannot display a logo
  // This prevents showing one brand's logo in another brand's context
  if (!brand?.theme?.logoUrl) {
    // Return empty container - no logo is better than wrong logo
    return <div className="logo-container" />;
  }

  const logoSrc = brand.theme.logoUrl;
  const altText = brand.productName || t('common.productFallback');

  return (
    <div className={`logo-container ${className}`}>
      <img
        src={logoSrc}
        alt={altText}
        className={`logo-image ${desktopEnhanced ? 'desktop-enhanced' : ''}`}
      />

      <style>{`
        .logo-container {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: ${designTokens.spacing.md}; /* Reduced from xl */
          
          /* Mobile-First: Ensure logo is always visible */
          min-height: 32px;
          width: 100%;
        }
        
        
        .logo-image {
          width: auto;
          max-width: ${designTokens.layout.logo.maxWidth};
          object-fit: contain;
          
          /* Mobile-First: Start with mobile size */
          height: ${designTokens.layout.logo.height.mobile};
          
          /* Performance optimizations */
          image-rendering: -webkit-optimize-contrast;
          image-rendering: crisp-edges;
        }
        
        /* Mobile optimizations (up to 767px) */
        ${designTokens.media.mobile} {
          .logo-container {
            margin-bottom: ${designTokens.spacing.sm};
          }
          
          .logo-image {
            height: 36px; /* Reduced for better space utilization */
            max-width: 240px;
          }
        }
        
        /* Tablet (768px-1023px) */
        ${designTokens.media.tablet} {
          .logo-container {
            margin-bottom: ${designTokens.spacing.lg}; /* Reduced from 2xl */
          }
          
          .logo-image {
            height: 44px; /* Slightly smaller than before */
            max-width: ${designTokens.layout.logo.maxWidth};
          }
        }
        
        /* Desktop (1024px+) */
        ${designTokens.media.desktop} {
          .logo-container {
            margin-bottom: ${designTokens.spacing.lg}; /* Reduced from 2xl */
          }
          
          .logo-image {
            height: 44px; /* Slightly smaller than before */
            max-width: ${designTokens.layout.logo.maxWidth};
          }
        }
        
        
        /* Desktop UX Enhancement - Now follows standard sizing */
        .logo-image.desktop-enhanced {
          /* Uses the same responsive sizes as defined above */
          /* This prevents over-scaling while maintaining the enhancement flag for compatibility */
        }
        
        /* High-DPI displays */
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
          .logo-image {
            image-rendering: auto;
          }
        }
      `}</style>
    </div>
  );
});
